import { Euler, Vector3 } from 'three';
import { SeededRandom } from '../core/SeededRandom';

export type Point3 = [number, number, number];
export interface ScenicBody { position: Point3; radius: number; type: number; rings?: boolean }
export interface CloudRegion {
  id: string; kind: string; position: Point3; color: string;
  nebula?: { extent: Point3; accent: string; seed: number };
  rotation?: Point3; profile?: 'filament' | 'shell'; strength?: number;
}

// These are scenery, not destinations. The renderer and collision model share
// this fixed layout; neither the camera nor navigation generates it on approach.
export const STAR_STREAM: CloudRegion = {
  id: 'stellar-rift', kind: 'nebula', position: [13000, -2300, 0],
  color: '#26334c', nebula: { extent: [4600, 3800, 22000], accent: '#7193b3', seed: 19.6 },
  rotation: [.32, -.12, .08], profile: 'filament', strength: .6,
};
export const SCENIC_CLOUDS: CloudRegion[] = [
  STAR_STREAM,
  { id: 'far-rift', kind: 'nebula', position: [-14000, 2600, -10000], color: '#35354f',
    nebula: { extent: [1800, 1100, 12500], accent: '#8d748e', seed: 44.3 }, rotation: [.5, .75, -.2], profile: 'filament', strength: .85 },
  { id: 'red-filaments', kind: 'nebula', position: [4800, 600, 8700], color: '#552b39',
    nebula: { extent: [700, 440, 2600], accent: '#cb8f86', seed: 73.2 }, rotation: [.55, 1.1, -.5], profile: 'filament', strength: .9 },
  { id: 'ion-shell', kind: 'nebula', position: [-4100, -1100, 3600], color: '#185d67',
    nebula: { extent: [520, 390, 500], accent: '#94d7cc', seed: 93.1 }, rotation: [.2, 0, .4], profile: 'shell', strength: .75 },
];

const normal = (r: SeededRandom) => Math.sqrt(-2 * Math.log(Math.max(.0001, r.next()))) * Math.cos(r.range(0, Math.PI * 2));
export function streamPosition(r: SeededRandom): Vector3 {
  const z = r.signed(.95), wave = Math.sin(z * 5.5) * .13;
  // Unequal stellar banks leave a broken dust lane instead of a uniform stripe.
  const bank = r.next() < .65 ? -.23 : .22;
  return new Vector3(normal(r) * 900, (wave + bank + normal(r) * .24) * 3800, z * 22000);
}
export function regionPoint(region: CloudRegion, point: Vector3) {
  if (region.rotation) point.applyEuler(new Euler(...region.rotation));
  return point.add(new Vector3(...region.position));
}

export const SCENIC_BODIES: ScenicBody[] = [
  { position: [720, -120, 340], radius: 19, type: 4 },
  { position: [772, -112, 366], radius: 4.4, type: 3 },
  { position: [1850, 140, -740], radius: 31, type: 6, rings: true },
  { position: [1790, 152, -690], radius: 5.5, type: 4 },
  { position: [-430, -130, -1100], radius: 23, type: 5 },
  { position: [-2500, -420, -1100], radius: 68, type: 6, rings: true },
  { position: [-2650, -398, -1170], radius: 12, type: 4 },
  { position: [3400, 320, 1700], radius: 47, type: 5 },
  { position: [3700, -660, -2950], radius: 65, type: 0 },
  { position: [3810, -620, -2990], radius: 11, type: 3 },
  { position: [-3500, 800, -6600], radius: 54, type: 4, rings: true },
  { position: [-3630, 840, -6550], radius: 9, type: 3 },
  { position: [6500, -500, -6800], radius: 110, type: 6, rings: true },
  { position: [6800, -460, -6900], radius: 24, type: 1 },
  { position: [10300, -1650, 4200], radius: 85, type: 5 },
  { position: [14600, -1100, -2400], radius: 130, type: 0, rings: true },
  { position: [15030, -1030, -2510], radius: 27, type: 4 },
  { position: [-7800, 1200, 3200], radius: 96, type: 6 },
  { position: [-8150, 1140, 3110], radius: 18, type: 3 },
  { position: [-10800, 2300, -10800], radius: 100, type: 5 },
  { position: [-6900, -2700, -14500], radius: 170, type: 4, rings: true },
  { position: [3300, 1700, 12000], radius: 110, type: 1 },
];

export const ASTEROID_STREAMS: Array<{ position: Point3; length: number; rotation: Point3 }> = [
  { position: [470, -130, -390], length: 720, rotation: [.3, -.4, -.18] },
  { position: [-660, 80, -1260], length: 580, rotation: [.2, 1.1, .3] },
  { position: [960, -60, -1740], length: 900, rotation: [-.1, -.7, .2] },
  { position: [3200, -280, 1350], length: 1500, rotation: [.3, .5, -.2] },
];
export function debrisPosition(r: SeededRandom, stream: typeof ASTEROID_STREAMS[number]) {
  const t = r.signed();
  return new Vector3(t * stream.length, Math.sin(t * 3.2) * 45 + r.signed(23), Math.sin(t * 2) * 65 + r.signed(42))
    .applyEuler(new Euler(...stream.rotation)).add(new Vector3(...stream.position));
}
const rockRandom = new SeededRandom(8201);
export const ASTEROIDS = ASTEROID_STREAMS.flatMap(stream => Array.from({ length: 48 }, () => ({
  position: debrisPosition(rockRandom, stream).toArray() as Point3,
  radius: rockRandom.range(.9, 5.6), rotation: [rockRandom.signed(3), rockRandom.signed(3), rockRandom.signed(3)] as Point3,
})));

export const STAR_CLUSTERS: Array<{ position: Point3; radius: number; seed: number; count: number }> = [
  { position: [5700, 800, -2900], radius: 340, seed: 208, count: 3800 },
  { position: [-5900, 1700, -900], radius: 490, seed: 361, count: 4400 },
  { position: [2800, -800, 7000], radius: 240, seed: 487, count: 2200 },
];
export const COMETS: ScenicBody[] = [
  { position: [2600, 680, -1300], radius: 3, type: 4 },
  { position: [-4700, 900, -5400], radius: 3, type: 4 },
];
